import type { SceneStore } from '@os/doc';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { EditorController } from './controller';
import type { AuthorsSource } from './authors';
import { Sheet } from '../../ui';
import { useSelectionProps } from './useSelectionProps';
import { ActionButtons, AuthorLine, ColorField, hasColor, HomeLine, OrderField, TypeFields } from './properties-fields';

export { COLORS } from './properties-fields';

/**
 * Every control of the selection in one panel: the phone's bottom sheet (P11), resting on the bottom toolbar. Desktop
 * and tablet show the context bar above the selection instead (P5f). Edits go through store commands only, so each
 * one is undoable. It comes and goes with the selection and never takes the focus; closing it (button, Escape, a
 * swipe down) clears the selection; a tap on another object keeps it open on that one (P16-2 Ruling 6).
 */
export function PropertiesPanel({
  controller,
  store,
  authors,
}: {
  controller: EditorController;
  store: SceneStore;
  /** Server boards: who added the selection (P5d). */
  authors?: AuthorsSource | null;
}) {
  const { t } = useTranslation();
  const p = useSelectionProps(controller, store);
  const body = useRef<HTMLDivElement>(null);
  // The sheet covers the lower part of the view: what is selected must stay above it (once per new selection).
  const selected = p?.ids.join(',') ?? '';
  useEffect(() => {
    const sheet = body.current?.closest<HTMLElement>('[role="region"]');
    if (!selected || !sheet) return;
    const raf = requestAnimationFrame(() => controller.revealSelection(Math.max(0, window.innerHeight - sheet.offsetTop)));
    return () => cancelAnimationFrame(raf);
  }, [controller, selected]);
  if (!p) return null;
  return (
    <Sheet
      title={t('panel.label')}
      closeLabel={t('common.close')}
      onClose={() => controller.setSelection([])}
      role="region"
      initialFocus={false}
      finalFocus={false}
      // On the bottom toolbar (6 px + 44 px buttons + the safe-area padding); z-10 as before its portal: the editor's
      // panels (full-screen comments, uploads, follow banner) stay above it.
      className="bottom-[calc(3.125rem+max(0.375rem,env(safe-area-inset-bottom)))] z-10 max-h-[40dvh]"
    >
      <div
        ref={body}
        // A click on the panel's buttons keeps the text editor focused (fields and selects still take the focus).
        onMouseDown={(e) => {
          if (!(
            e.target instanceof HTMLInputElement ||
            e.target instanceof HTMLSelectElement ||
            e.target instanceof HTMLTextAreaElement
          ))
            e.preventDefault();
        }}
        className="flex flex-col gap-3 pb-1"
      >
        {authors && <AuthorLine source={authors} objects={p.objects} />}
        <HomeLine p={p} />
        {hasColor(p) && <ColorField p={p} store={store} />}
        <TypeFields p={p} store={store} />
        <OrderField p={p} store={store} />
        <ActionButtons p={p} store={store} controller={controller} />
      </div>
    </Sheet>
  );
}
